import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { dashboardService, OutcomesPayload } from '../../services/DashboardService';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import styles from './PotentialOutcomes.module.css';

const PotentialOutcomes: React.FC = () => {
  const [data, setData] = useState<OutcomesPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const d = await dashboardService.getOutcomes();
      setData(d);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to load outcome data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fmt = (val: number, decimals = 2) =>
    (val || 0).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  const pnlClass = (val: number) => (val > 0 ? styles.positive : val < 0 ? styles.negative : '');

  if (loading) {
    return (
      <section className={styles.page}>
        <SignalLoader message="Loading potential outcomes..." fullPage />
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className={styles.page}>
        <p className={styles.error}>{error || 'No outcome data available'}</p>
        <Link to="/home" className={styles.back}>
          &larr; Back to Home
        </Link>
      </section>
    );
  }

  const strategies = data.strategies || [];
  const prediction = data.prediction || {};

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Potential Outcomes</h1>
        <span className={styles.subtitle}>Given today's market, what does history say about likely outcomes?</span>
      </div>

      {/* Prediction context */}
      {prediction.direction && (
        <div className={styles.contextBar}>
          <div className={styles.contextItem}>
            <span className={styles.contextLabel}>Prediction</span>
            <span
              className={`${styles.contextValue} ${prediction.direction === 'BULL' ? styles.positive : styles.negative}`}
            >
              {prediction.direction}
            </span>
          </div>
          <div className={styles.contextItem}>
            <span className={styles.contextLabel}>Confidence</span>
            <span className={styles.contextValue}>{fmt(prediction.confidence * 100, 1)}%</span>
          </div>
          <div className={styles.contextItem}>
            <span className={styles.contextLabel}>Raw Score</span>
            <span className={styles.contextValue}>{fmt(prediction.raw_score, 2)}</span>
          </div>
          {prediction.risk_flags?.length > 0 && (
            <div className={styles.contextItem}>
              <span className={styles.contextLabel}>Risk Flags</span>
              <span className={styles.contextValue} style={{ fontSize: 14 }}>
                {prediction.risk_flags.join(', ')}
              </span>
            </div>
          )}
        </div>
      )}

      <div className={styles.disclaimer}>
        Based on historical backtest trades. Past performance does not guarantee future results.
      </div>

      {/* Strategy outcome table */}
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Strategy</th>
              <th>Type</th>
              <th>Hold Period</th>
              <th>Trades</th>
              <th>Win Rate</th>
              <th>Avg Win</th>
              <th>Avg Loss</th>
              <th>Total P&L</th>
              <th>Profit Factor</th>
            </tr>
          </thead>
          <tbody>
            {strategies.map((s, i) => {
              const stats = s.historical_stats;
              const hasTrades = stats.total_trades > 0;
              return (
                <tr key={i} className={!hasTrades ? styles.noData : ''}>
                  <td className={styles.strategyCell}>{s.name}</td>
                  <td className={styles.typeCell}>{s.strategy_type}</td>
                  <td>{s.hold_period}</td>
                  <td>{stats.total_trades}</td>
                  <td>{hasTrades ? `${fmt(stats.win_rate, 1)}%` : '—'}</td>
                  <td className={styles.positive}>
                    {stats.avg_win
                      ? `$${fmt(stats.avg_win)}`
                      : stats.avg_win_pct
                        ? `${fmt(stats.avg_win_pct, 2)}%`
                        : '—'}
                  </td>
                  <td className={styles.negative}>
                    {stats.avg_loss
                      ? `-$${fmt(Math.abs(stats.avg_loss))}`
                      : stats.avg_loss_pct
                        ? `${fmt(stats.avg_loss_pct, 2)}%`
                        : '—'}
                  </td>
                  <td className={pnlClass(stats.total_pnl || 0)}>
                    {stats.total_pnl != null ? `$${fmt(stats.total_pnl)}` : '—'}
                  </td>
                  <td>{stats.profit_factor ? fmt(stats.profit_factor, 2) : '—'}</td>
                </tr>
              );
            })}
            {/* Do Nothing row */}
            <tr className={styles.doNothingRow}>
              <td className={styles.strategyCell}>Do Nothing</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>—</td>
              <td>$0.00</td>
              <td>—</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Distribution charts */}
      {strategies.filter((s) => s.historical_stats.trade_pnl_distribution?.buckets?.length > 0).length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>P&L Distributions</h2>
          <div className={styles.distGrid}>
            {strategies
              .filter((s) => s.historical_stats.trade_pnl_distribution?.buckets?.length > 0)
              .map((s, i) => {
                const dist = s.historical_stats.trade_pnl_distribution;
                const chartData = dist.buckets.map((b) => ({
                  range: `${b.lo.toFixed(1)}`,
                  count: b.count,
                  isNeg: b.hi <= 0,
                }));
                return (
                  <div key={i} className={styles.distCard}>
                    <h3 className={styles.distTitle}>
                      {s.name}
                      {dist.mean != null && (
                        <span className={styles.distSubtitle}>
                          {' '}
                          — mean: ${fmt(dist.mean, 2)}, median: ${fmt(dist.median || 0, 2)}
                        </span>
                      )}
                    </h3>
                    <ResponsiveContainer width="100%" height={180}>
                      <BarChart data={chartData}>
                        <XAxis dataKey="range" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{
                            background: 'var(--bg-card)',
                            border: '1px solid var(--border-primary)',
                            fontSize: 12,
                          }}
                        />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                          {chartData.map((b, j) => (
                            <Cell
                              key={j}
                              fill={b.isNeg ? 'var(--color-negative)' : 'var(--color-positive)'}
                              opacity={0.8}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })}
          </div>
        </>
      )}

      <Link to="/home" className={styles.back}>
        &larr; Back to Home
      </Link>
    </section>
  );
};

export default PotentialOutcomes;
